import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/shared/api/queryKeys'
import { setCurrencyCode, setDisplayTimeZone } from '@/shared/lib/format'
import { AuthContext, type AuthContextValue } from './AuthContext'
import {
  chooseTenant,
  confirmTotp,
  fetchMe,
  login,
  logout,
  verifyTotp,
  type CurrentUser,
  type LoginOutcome,
  type LoginSuccess,
  type TotpOutcome,
} from './authApi'
import { clearSession, hasValidToken, saveSession } from './tokenStorage'

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  // Token localStorage'da, lekin React uni "ko'rmaydi" — shuning uchun
  // holatga nusxalanadi. Aks holda kirgandan keyin daraxt qayta chizilmaydi.
  const [hasToken, setHasToken] = useState(hasValidToken)

  const meQuery = useQuery<CurrentUser>({
    queryKey: queryKeys.me,
    queryFn: fetchMe,
    enabled: hasToken,
    // Kim ekanimiz sessiya davomida o'zgarmaydi
    staleTime: Infinity,
    retry: false,
  })

  const user = meQuery.data ?? null

  /**
   * VALYUTA VA VAQT ZONASI SHU YERDA, ILDIZDA O'RNATILADI.
   *
   * `formatMoney` va `formatDateTime` qiymatni modul darajasidagi
   * o'zgaruvchidan o'qiydi (`shared/lib/format.ts`) — u React holati
   * EMAS va REAKTIV EMAS. Pastroqda o'rnatilsa, uni so'ramagan
   * ekranlarda summalar ZAXIRA valyutada chiqadi.
   *
   * ⚠️ KASSA EKRANIDA BU CHIROY EMAS. Kassir ekrandagi summani o'qib
   * naqd pul oladi; birlik noto'g'ri chiqsa yoki chizilgandan keyin
   * SAKRAB o'zgarsa, u boshqa summani olishi mumkin.
   *
   * NEGA `useEffect` EMAS: effekt birinchi chizilishdan KEYIN ishlaydi,
   * ya'ni bolalar bir marta eski qiymat bilan chizilib ulgurardi. Amal
   * idempotent — qayta chizilishda takrorlanishi zararsiz.
   *
   * Ikkinchi yarmi `RequireAuth` da: `me` yuklanmaguncha himoyalangan
   * ekranlar UMUMAN chizilmaydi.
   */
  if (user) {
    setCurrencyCode(user.currency)
    setDisplayTimeZone(user.dayTimezone)
  }

  /**
   * Sessiyani yakunlaydi — kirish oqimining qaysi yo'lidan yakuniy
   * tokenga yetib kelinsa ham (TOTP sozlash, TOTP tekshiruv, tashkilot
   * tanlash) oxirida shu BITTA joyga kelib qo'shiladi.
   *
   * ⚠️ Yagona yozish nuqtasi bo'lgani muhim: refresh tokenini saqlashni
   * uch shoxdan birida unutish yetardi va chiqish o'sha yo'ldan
   * kirganlarda JIMGINA ishlamay qolardi (`authApi.logout` uni
   * majburiy talab qiladi).
   *
   * ⚠️ Kirish javobi `user` ni ALLAQACHON o'z ichiga oladi
   * (`LoginStepPresenter`) — uni keshga qo'yamiz va `/api/me` ni
   * qayta so'ramaymiz. Eski foydalanuvchining keshi ham shu bilan
   * almashadi.
   */
  const finishLogin = useCallback(
    (result: LoginSuccess) => {
      saveSession(result.token, result.expiresAt, result.refreshToken)
      setHasToken(true)
      queryClient.setQueryData(queryKeys.me, result.user)
    },
    [queryClient],
  )

  const signIn = useCallback(
    async (username: string, password: string): Promise<LoginOutcome> => login({ username, password }),
    [],
  )

  /**
   * TOTP javobini yakunlaydi — natija UCH XIL bo'lishi mumkin: bitta
   * a'zolik bo'lsa sessiya shu yerda saqlanadi, ko'p a'zolik yoki
   * a'zolik yo'q bo'lsa sessiya SAQLANMAYDI va natija chaqiruvchiga
   * (`TotpPage`) qaytariladi.
   */
  const finishTotp = useCallback(
    (outcome: TotpOutcome): TotpOutcome => {
      if (outcome.kind === 'authenticated') finishLogin(outcome)
      return outcome
    },
    [finishLogin],
  )

  const completeTotpSetup = useCallback(
    async (setupToken: string, code: string) => finishTotp(await confirmTotp(setupToken, code)),
    [finishTotp],
  )

  const completeTotpVerify = useCallback(
    async (totpToken: string, code: string) => finishTotp(await verifyTotp(totpToken, code)),
    [finishTotp],
  )

  const completeTenantChoice = useCallback(
    async (choiceToken: string, tenantId: string) => {
      finishLogin(await chooseTenant(choiceToken, tenantId))
    },
    [finishLogin],
  )

  const signOut = useCallback(async () => {
    await logout()
    clearSession()
    setHasToken(false)
    queryClient.clear()
  }, [queryClient])

  const can = useCallback(
    (permission: string): boolean => user?.permissions.has(permission) ?? false,
    [user],
  )

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: hasToken && user !== null,
      isLoading: hasToken && meQuery.isPending,
      error: meQuery.error,
      signIn,
      completeTotpSetup,
      completeTotpVerify,
      completeTenantChoice,
      signOut,
      can,
    }),
    [
      user,
      hasToken,
      meQuery.isPending,
      meQuery.error,
      signIn,
      completeTotpSetup,
      completeTotpVerify,
      completeTenantChoice,
      signOut,
      can,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
